import { useState } from 'react';
import { Target, TrendingUp, TrendingDown, Minus, Loader2 } from 'lucide-react';
import { Button } from '@verlo/shared/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@verlo/shared/components/ui/select';
import { reportsIntelApi } from '@verlo/shared/services/api';
import { toast } from 'sonner';

const KINDS = [
  { key: 'prev_period',   label: 'Previous period' },
  { key: 'same_day_ly',   label: 'Same period LY' },
  { key: 'budget',        label: 'Budget' },
  { key: 'forecast',      label: 'Forecast' },
  { key: 'group_average', label: 'Group average' },
  { key: 'best_site',     label: 'Best site' },
  { key: 'target_kpi',    label: 'Target KPI' },
];

const fmtMoney = (v) => v == null ? '—' : `£${Math.round(v).toLocaleString('en-GB')}`;
const fmtPct = (v) => v == null ? '—' : `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`;

const BenchmarkPicker = ({ metric, currentValue, siteId, period, format = 'gbp', label = 'Benchmark' }) => {
  const [kind, setKind] = useState('prev_period');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!period?.start || !period?.end) { toast.error('Missing period'); return; }
    setLoading(true);
    try {
      const res = await reportsIntelApi.benchmark({
        metric, current_value: currentValue,
        site_id: siteId && siteId !== 'all' ? siteId : null,
        period, kind,
      });
      setResult(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Benchmark failed');
    } finally { setLoading(false); }
  };

  const DirectionIcon = result?.direction === 'good' ? TrendingUp : result?.direction === 'bad' ? TrendingDown : Minus;
  const dirColor = result?.direction === 'good' ? 'text-signal-ok' : result?.direction === 'bad' ? 'text-signal-stop' : 'text-ink-soft';

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="benchmark-picker">
      <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold text-ink-soft">
        <Target size={11} /> {label}
      </div>
      <Select value={kind} onValueChange={(v) => { setKind(v); setResult(null); }}>
        <SelectTrigger className="h-8 w-[170px] text-[12px]" data-testid="benchmark-kind"><SelectValue /></SelectTrigger>
        <SelectContent>
          {KINDS.map((k) => <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button size="sm" onClick={run} disabled={loading} className="h-8 px-2.5 text-[12.5px] bg-verlo hover:bg-verlo-hover" data-testid="benchmark-run">
        {loading ? <Loader2 size={11} className="animate-spin mr-1" /> : null}
        Compare
      </Button>
      {result && result.baseline != null && (
        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-hairline rounded-md" data-testid="benchmark-result">
          <span className="text-[12.5px] text-ink-soft">{result.label}</span>
          <span className="font-mono-display text-[12px] font-semibold text-ink">
            {format === 'gbp' ? fmtMoney(result.baseline) : result.baseline}
          </span>
          <span className={`inline-flex items-center gap-0.5 font-mono-display text-[12.5px] font-semibold ${dirColor}`}>
            <DirectionIcon size={11} />
            {result.delta_pct != null ? fmtPct(result.delta_pct) : '—'}
          </span>
        </div>
      )}
      {result && result.baseline == null && (
        <span className="text-[12.5px] text-ink-soft italic" data-testid="benchmark-no-baseline">No baseline available for {result.label.toLowerCase()}</span>
      )}
    </div>
  );
};

export default BenchmarkPicker;
